import type {ReactElement} from 'react';
import React, {useCallback} from 'react';
import {Button, IconPlacement, Size, Type} from '@Components/button';
// eslint-disable-next-line import/no-extraneous-dependencies
import useEyeDropper from 'use-eye-dropper';
import {Icon} from "@Components/icon-v2";
import styles from "./eyedropper-color-picker.module.scss";
import {IconShape, IconSize, IconType} from "@Components/icon-v2/icon.types";

interface EyedropperColorPickerProps {
  onColorPicked: (color: string) => void;
  onPickStart?: VoidFunction;
  onPickEnd?: VoidFunction;
  customClasses?: string;
  ctaType?: EyeDropperCTAType;
}

export enum EyeDropperCTAType {
  ICON,
  BUTTON,
}

export function EyedropperColorPicker({ctaType = EyeDropperCTAType.BUTTON, ...props}: EyedropperColorPickerProps): ReactElement<any> | null {
  const {open, isSupported} = useEyeDropper();

  const invokeOnPickStart = (): void => {
    if (props.onPickStart) {
      props.onPickStart();
    }
  };

  const invokeOnPickEnd = (): void => {
    if (props.onPickEnd) {
      props.onPickEnd();
    }
  };

  const pickColor = useCallback(() => {
    const openPicker = async (): Promise<void> => {
      try {
        invokeOnPickStart();
        const pickedColor = await open();
        props.onColorPicked(pickedColor.sRGBHex);
        invokeOnPickEnd();
      } catch (e) {
        console.log(e);
      }
    };

    void openPicker();
  }, [open]);

  if (!isSupported()) {
    return null;
  }

  const getEyeDropperCTA = (): ReactElement<any> => {
    if (ctaType === EyeDropperCTAType.ICON) {
      return (
        <Icon
          onClick={pickColor}
          className={`${styles.pickerIcon} spacing-m-l-2`}
          icon="icon-colorpicker"
          size={IconSize.SIZE_ICON_20}
          shape={IconShape.SQUARE}
          type={IconType.SECONDARY}
        />
      )
    }

    return (
      <Button
        customClasses={props.customClasses}
        type={Type.SECONDARY}
        size={Size.SMALL}
        text={window.i18next.t('pmwjs_picker')}
        iconPlacement={IconPlacement.LEFT}
        icon="icon-colorpicker"
        onClick={pickColor}
      />
    )
  };

  return getEyeDropperCTA();
}
